/**
 * FERP Portal - Form Gallery Page
 *
 * Surfaces ALL form components from @ferp/workflow-forms enhanced/ directory
 * so architects can visually review each form and decide what to keep.
 *
 * Categories:
 * - Enhanced Primitive Forms (16): Per-primitive specialized views
 * - Validated Forms (11): Create/edit forms with AJV validation
 * - Manager Components (8): Full CRUD with GraphQL persistence
 * - Comprehensive Forms (2): 185+ field power-user forms
 *
 * Architecture: Architect Layer (Layer 3) - DRR-074 migration review tool
 */

import React, { useState, useMemo, Suspense } from 'react';
import {
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  NavLink,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
  Alert,
} from '@mantine/core';
import {
  IconSearch,
  IconAlertTriangle,
  IconForms,
  IconEdit,
  IconDatabase,
  IconStar,
} from '@tabler/icons-react';

// ============================================================================
// Component registry: lazy-loaded from @ferp/workflow-forms/enhanced
// ============================================================================

interface FormEntry {
  id: string;
  label: string;
  category: string;
  description: string;
  component: React.LazyExoticComponent<React.ComponentType<unknown>>;
  props?: Record<string, unknown>;
  verdict: 'KEEP' | 'SUPERSEDED' | 'DISCARD' | 'DEMO';
  loc: number;
  /** Components that import from useWorkflowState need the EnhancedComponentProvider wrapper */
  needsProvider?: boolean;
}

/**
 * Gallery-mode workflow context provider.
 *
 * Enhanced components call useWorkflowState() which requires WorkflowContext.
 * We can't use EnhancedComponentProvider (depends on useMonitoring) or
 * WorkflowStateProvider (depends on @ferp/auth's AuthProvider which isn't in
 * the portal tree). Instead, provide WorkflowContext directly with stubs so
 * the components render their UI shells with empty data.
 */
const GalleryProvider = React.lazy(async () => {
  const { WorkflowContext } = await import('@ferp/workflow-forms/state/WorkflowStateProvider');

  const noop = () => {};
  const noopAsync = async () => undefined as unknown;
  const stubEntityOps = {
    fetch: noopAsync,
    create: noopAsync,
    update: noopAsync,
    delete: noopAsync,
    setFilter: noop,
    setSorting: noop,
    setSelections: noop,
  };
  const emptyEntityState = {
    entities: {},
    ids: [],
    loading: { list: false, create: false, update: false, delete: false },
    error: null,
    filters: { search: '', status: [], assignee: [], dateRange: null, custom: {} },
    sorting: { field: 'createdAt', order: 'desc' as const },
    selections: [],
    lastUpdated: null,
  };
  const stubValue = {
    state: {
      definitions: emptyEntityState,
      instances: emptyEntityState,
      tasks: emptyEntityState,
      signals: emptyEntityState,
      policies: emptyEntityState,
      ui: {
        sidebarCollapsed: false, theme: 'light' as const, currentView: 'dashboard' as const,
        breadcrumbs: [], modals: [], notifications: [], forms: [], panels: [],
        renderOptimization: true,
      },
      realtime: { connected: false, connectionId: null, subscriptions: [], updates: [], conflicts: [] },
      history: { past: [], present: {} as unknown as import('@ferp/workflow-forms/state/WorkflowStateProvider').WorkflowState, future: [], maxHistorySize: 50, groupingDelay: 500, lastAction: null, lastActionTime: 0 },
      sync: {
        online: true, syncing: false, syncQueue: [], lastSync: null, syncErrors: [], pendingOperations: [],
        conflictResolution: { conflicts: [], strategy: 'client_wins' as const, resolvedConflicts: [] },
      },
      metrics: { renderCount: 0, lastRenderTime: 0, actionCounts: {}, performanceMarks: [] },
    },
    dispatch: noop as unknown as React.Dispatch<import('@ferp/workflow-forms/state/WorkflowStateProvider').WorkflowAction>,
    definitions: stubEntityOps,
    instances: stubEntityOps,
    tasks: stubEntityOps,
    signals: stubEntityOps,
    policies: stubEntityOps,
    ui: {
      setSidebarCollapsed: noop, setTheme: noop, setCurrentView: noop,
      setBreadcrumbs: noop, addModal: () => '', removeModal: noop,
      addNotification: () => '', removeNotification: noop,
    },
    history: { undo: noop, redo: noop, canUndo: false, canRedo: false, clear: noop },
    realtime: { connect: noop, disconnect: noop, subscribe: () => '', unsubscribe: noop, handleUpdate: noop },
    sync: { setOnline: noop, addPendingOperation: noop, removePendingOperation: noop, resolveConflict: noop },
    selectors: {
      getEntity: () => undefined, getEntities: () => [], getFilteredEntities: () => [],
      getSortedEntities: () => [], getSelectedEntities: () => [], getMetrics: () => ({ renderCount: 0, lastRenderTime: 0, actionCounts: {}, performanceMarks: [] }),
    },
  };

  const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <WorkflowContext.Provider value={stubValue as unknown as import('@ferp/workflow-forms/state/WorkflowStateProvider').WorkflowContextValue}>{children}</WorkflowContext.Provider>
  );

  return { default: Provider };
});

// Lazy-load helpers
const lazyNamed = <T extends Record<string, unknown>>(
  factory: () => Promise<T>,
  name: keyof T,
) =>
  React.lazy(async () => {
    const mod = await factory();
    return { default: mod[name] as React.ComponentType<unknown> };
  });

/**
 * All form components from enhanced/ directory.
 * Organized by generation/category for visual review.
 */
const FORM_COMPONENTS: FormEntry[] = [
  // ── Enhanced Primitive Forms ──────────────────────────────────────
  {
    id: 'task-enhanced',
    label: 'TaskEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Task lifecycle with real-time subscriptions, assignment matrix, due date tracking',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'TaskEnhanced'),
    verdict: 'KEEP',
    loc: 307,
    needsProvider: true,
  },
  {
    id: 'policy-enhanced',
    label: 'PolicyEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Multi-type policy routing, rule composition, CEL expression editor, compliance scoring',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'PolicyEnhanced'),
    verdict: 'KEEP',
    loc: 1000,
    needsProvider: true,
  },
  {
    id: 'instance-enhanced',
    label: 'InstanceEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'State machine visualization, parent-child instances, context editor, signal routing',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'InstanceEnhanced'),
    verdict: 'KEEP',
    loc: 950,
    needsProvider: true,
  },
  {
    id: 'signal-enhanced',
    label: 'SignalEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Signal templates, broadcast/targeted/filtered routing, delivery tracking, event bus',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'SignalEnhanced'),
    verdict: 'KEEP',
    loc: 856,
    needsProvider: true,
  },
  {
    id: 'schedule-enhanced',
    label: 'ScheduleEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Visual cron builder with 12 templates, timezone support, conflict detection',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ScheduleEnhanced'),
    verdict: 'KEEP',
    loc: 997,
    needsProvider: true,
  },
  {
    id: 'escalation-enhanced',
    label: 'EscalationEnhanced',
    category: 'Enhanced Primitive Forms',
    description: '4-level escalation paths, SLA config per priority, notification templates',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'EscalationEnhanced'),
    verdict: 'KEEP',
    loc: 211,
  },
  {
    id: 'jobstatus-enhanced',
    label: 'JobStatusEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Job queue monitoring, real-time progress, resource usage, prioritized queues, logs',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'JobStatusEnhanced'),
    verdict: 'KEEP',
    loc: 1083,
    needsProvider: true,
  },
  {
    id: 'workflow-definition-enhanced',
    label: 'WorkflowDefinitionEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'State machine designer, transition builder, guards/actions, retry policies, diagrams',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'WorkflowDefinitionEnhanced'),
    verdict: 'KEEP',
    loc: 848,
    needsProvider: true,
  },
  {
    id: 'timeline-item-enhanced',
    label: 'TimelineItemEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Audit timeline with event hierarchy, Gantt chart, analytics, CSV/JSON/PDF export',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'TimelineItemEnhanced'),
    verdict: 'KEEP',
    loc: 871,
    needsProvider: true,
  },
  {
    id: 'resource-enhanced',
    label: 'ResourceEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Resource allocation, utilization, reservations (hardcoded static data)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ResourceEnhanced'),
    verdict: 'DISCARD',
    loc: 137,
  },
  {
    id: 'integration-enhanced',
    label: 'IntegrationEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Endpoints, webhooks, auth forms, integration status cards (hardcoded)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'IntegrationEnhanced'),
    verdict: 'SUPERSEDED',
    loc: 189,
  },
  {
    id: 'context-enhanced',
    label: 'ContextEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Variable scoping, context inheritance, JSON config editor (minimal)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ContextEnhanced'),
    verdict: 'SUPERSEDED',
    loc: 81,
  },
  {
    id: 'execution-result-enhanced',
    label: 'ExecutionResultEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Execution metrics, error categories, performance bottleneck analysis (no real data)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ExecutionResultEnhanced'),
    verdict: 'SUPERSEDED',
    loc: 374,
  },
  {
    id: 'actor-descriptor-enhanced',
    label: 'ActorDescriptorEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Actor cards, roles, permission matrix (minimal hardcoded)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ActorDescriptorEnhanced'),
    verdict: 'DISCARD',
    loc: 108,
  },
  {
    id: 'transition-catalog-enhanced',
    label: 'TransitionCatalogEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'Transition template library, guard/action registry (skeleton)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'TransitionCatalogEnhanced'),
    verdict: 'DISCARD',
    loc: 77,
  },
  {
    id: 'optimization-recommendation-enhanced',
    label: 'OptimizationRecommendationEnhanced',
    category: 'Enhanced Primitive Forms',
    description: 'AI optimization wrapper (delegates to OptimizationRecommendationViewer)',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'OptimizationRecommendationEnhanced'),
    verdict: 'DISCARD',
    loc: 65,
  },

  // ── Validated Forms ───────────────────────────────────────────────
  {
    id: 'validated-task',
    label: 'ValidatedTaskForm',
    category: 'Validated Forms',
    description: 'Task creation with AJV JSON Schema validation + real-time feedback',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedTaskForm'),
    verdict: 'KEEP',
    loc: 410,
  },
  {
    id: 'validated-policy',
    label: 'ValidatedPolicyForm',
    category: 'Validated Forms',
    description: 'Policy form with rule editor, test modal with timeline, 3-tab layout',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedPolicyForm'),
    verdict: 'KEEP',
    loc: 710,
  },
  {
    id: 'validated-signal',
    label: 'ValidatedSignalForm',
    category: 'Validated Forms',
    description: 'Signal creation with payload JSON editor, optional metadata',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedSignalForm'),
    verdict: 'KEEP',
    loc: 317,
  },
  {
    id: 'validated-context',
    label: 'ValidatedContextForm',
    category: 'Validated Forms',
    description: 'Context hierarchy with inheritance, scoped variables, per-field encryption flags',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedContextForm'),
    verdict: 'KEEP',
    loc: 727,
    needsProvider: true,
  },
  {
    id: 'validated-schedule',
    label: 'ValidatedScheduleForm',
    category: 'Validated Forms',
    description: 'Schedule config with cron expression preview, timezone, retry policy',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedScheduleForm'),
    verdict: 'KEEP',
    loc: 425,
  },
  {
    id: 'validated-resource',
    label: 'ValidatedResourceForm',
    category: 'Validated Forms',
    description: 'Resource capacity with RingProgress charts, constraints, maintenance windows',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedResourceForm'),
    verdict: 'KEEP',
    loc: 850,
  },
  {
    id: 'validated-integration',
    label: 'ValidatedIntegrationForm',
    category: 'Validated Forms',
    description: 'Integration config with 5 auth types, test connection modal, webhook events',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedIntegrationForm'),
    verdict: 'KEEP',
    loc: 809,
    needsProvider: true,
  },
  {
    id: 'validated-escalation',
    label: 'ValidatedEscalationForm',
    category: 'Validated Forms',
    description: 'Escalation levels with actions, SLA penalties, notification config',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedEscalationForm'),
    verdict: 'KEEP',
    loc: 850,
    needsProvider: true,
  },
  {
    id: 'validated-jobstatus',
    label: 'ValidatedJobStatusForm',
    category: 'Validated Forms',
    description: 'Job details with 3 modes (create/edit/view), live logs in view mode',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedJobStatusForm'),
    verdict: 'KEEP',
    loc: 669,
  },
  {
    id: 'validated-complex-process',
    label: 'ValidatedComplexProcessForm',
    category: 'Validated Forms',
    description: 'Complex process with stages, actors, connections, JSON data preview',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ValidatedComplexProcessForm'),
    verdict: 'KEEP',
    loc: 512,
  },
  {
    id: 'validated-optimization',
    label: 'ValidatedOptimizationRecommendationForm',
    category: 'Validated Forms',
    description: 'AI optimization recommendation form with security boundary guard',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'ValidatedOptimizationRecommendationForm',
    ),
    verdict: 'KEEP',
    loc: 182,
  },

  // ── Manager Components ────────────────────────────────────────────
  {
    id: 'policy-manager',
    label: 'PolicyManager',
    category: 'Manager Components',
    description: 'Full CRUD for policies with GraphQL persistence, rule editor, test harness',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'PolicyManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 664,
  },
  {
    id: 'escalation-manager',
    label: 'EscalationManager',
    category: 'Manager Components',
    description: 'Escalation CRUD with timeline visualization, level management, SLA penalties',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'EscalationManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 666,
  },
  {
    id: 'integration-manager',
    label: 'IntegrationManager',
    category: 'Manager Components',
    description: 'Integration CRUD with connection health monitoring, webhook testing',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'IntegrationManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 1546,
  },
  {
    id: 'resource-manager',
    label: 'ResourceManager',
    category: 'Manager Components',
    description: 'Resource CRUD with capacity allocation management, usage gauges',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ResourceManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 769,
  },
  {
    id: 'schedule-manager',
    label: 'ScheduleManager',
    category: 'Manager Components',
    description: 'Schedule CRUD with cron preview, execution history, next-run predictions',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ScheduleManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 754,
  },
  {
    id: 'communication-manager',
    label: 'CommunicationManager',
    category: 'Manager Components',
    description: 'Communication channels, notification templates, delivery tracking',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'CommunicationManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 806,
  },
  {
    id: 'domain-config-manager',
    label: 'DomainConfigurationManager',
    category: 'Manager Components',
    description: 'Multi-tenant domain configuration, subgraph settings, nested configs',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'DomainConfigurationManager'),
    props: { tenant: 'demo-tenant' },
    verdict: 'KEEP',
    loc: 837,
  },
  {
    id: 'complex-process-manager',
    label: 'ComplexProcessManager',
    category: 'Manager Components',
    description: 'Process execution monitoring, version control, stage progress',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ComplexProcessManager'),
    verdict: 'KEEP',
    loc: 449,
  },

  // ── Comprehensive Forms ───────────────────────────────────────────
  {
    id: 'comprehensive-task-form',
    label: 'ComprehensiveTaskForm',
    category: 'Comprehensive Forms',
    description: '185+ field Task form with dual control, MFA, compliance, escalation, audit trails',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ComprehensiveTaskForm'),
    props: { mode: 'create' },
    verdict: 'KEEP',
    loc: 2426,
  },
  {
    id: 'comprehensive-task-demo',
    label: 'ComprehensiveTaskFormDemo',
    category: 'Comprehensive Forms',
    description: '5 interactive scenarios: Healthcare, Financial, Hierarchy, Escalation, Automation',
    component: lazyNamed(() => import('@ferp/workflow-forms/enhanced'), 'ComprehensiveTaskFormDemo'),
    verdict: 'DEMO',
    loc: 728,
  },
];

// ============================================================================
// Category icons
// ============================================================================

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Enhanced Primitive Forms': <IconForms size={16} />,
  'Validated Forms': <IconEdit size={16} />,
  'Manager Components': <IconDatabase size={16} />,
  'Comprehensive Forms': <IconStar size={16} />,
};

// ============================================================================
// Verdict badge colors
// ============================================================================

const VERDICT_COLORS: Record<string, string> = {
  KEEP: 'green',
  SUPERSEDED: 'yellow',
  DISCARD: 'red',
  DEMO: 'blue',
};

// ============================================================================
// Error boundary
// ============================================================================

class FormErrorBoundary extends React.Component<
  { children: React.ReactNode; componentId: string },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Component failed to load">
          <Text size="sm">
            <strong>{this.props.componentId}</strong>: {this.state.error.message}
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            This component may need props or context not available in gallery mode.
          </Text>
        </Alert>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// Main Form Gallery Page
// ============================================================================

export function FormGalleryPage() {
  const [selectedId, setSelectedId] = useState<string>('task-enhanced');
  const [search, setSearch] = useState('');
  const [filterVerdict, setFilterVerdict] = useState<string>('all');

  const filtered = useMemo(() => {
    return FORM_COMPONENTS.filter((c) => {
      const matchesSearch =
        !search ||
        c.label.toLowerCase().includes(search.toLowerCase()) ||
        c.description.toLowerCase().includes(search.toLowerCase());
      const matchesVerdict = filterVerdict === 'all' || c.verdict === filterVerdict;
      return matchesSearch && matchesVerdict;
    });
  }, [search, filterVerdict]);

  const grouped = useMemo(() => {
    const map = new Map<string, FormEntry[]>();
    for (const c of filtered) {
      const list = map.get(c.category) ?? [];
      list.push(c);
      map.set(c.category, list);
    }
    return map;
  }, [filtered]);

  const selected = FORM_COMPONENTS.find((c) => c.id === selectedId);

  // Stats
  const stats = useMemo(() => ({
    total: FORM_COMPONENTS.length,
    keep: FORM_COMPONENTS.filter((c) => c.verdict === 'KEEP').length,
    superseded: FORM_COMPONENTS.filter((c) => c.verdict === 'SUPERSEDED').length,
    discard: FORM_COMPONENTS.filter((c) => c.verdict === 'DISCARD').length,
    demo: FORM_COMPONENTS.filter((c) => c.verdict === 'DEMO').length,
    totalLoc: FORM_COMPONENTS.reduce((sum, c) => sum + c.loc, 0),
  }), []);

  return (
    <Box style={{ display: 'flex', height: 'calc(100vh - 120px)' }}>
      {/* Sidebar */}
      <Box
        style={{
          width: 340,
          minWidth: 340,
          borderRight: '1px solid var(--mantine-color-default-border)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack gap="xs" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Group justify="space-between">
            <Title order={5}>Form Gallery</Title>
            <Group gap={4}>
              <Badge variant="light" color="green" size="xs">{stats.keep} keep</Badge>
              <Badge variant="light" color="yellow" size="xs">{stats.superseded} super</Badge>
              <Badge variant="light" color="red" size="xs">{stats.discard} drop</Badge>
            </Group>
          </Group>
          <Text size="xs" c="dimmed">
            {stats.total} forms | {stats.totalLoc.toLocaleString()} LOC | DRR-074 migration review
          </Text>
          <TextInput
            placeholder="Search forms..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
          />
          <SegmentedControl
            size="xs"
            value={filterVerdict}
            onChange={setFilterVerdict}
            data={[
              { label: 'All', value: 'all' },
              { label: 'Keep', value: 'KEEP' },
              { label: 'Superseded', value: 'SUPERSEDED' },
              { label: 'Discard', value: 'DISCARD' },
            ]}
            fullWidth
          />
        </Stack>

        <ScrollArea style={{ flex: 1 }} p="xs">
          <Stack gap={2}>
            {[...grouped.entries()].map(([category, items]) => (
              <NavLink
                key={category}
                label={
                  <Group gap="xs">
                    <Text size="xs" fw={600}>{category}</Text>
                    <Badge size="xs" variant="light">{items.length}</Badge>
                  </Group>
                }
                leftSection={CATEGORY_ICONS[category] ?? <IconForms size={16} />}
                childrenOffset={16}
                defaultOpened={items.some((i) => i.id === selectedId)}
              >
                {items.map((item) => (
                  <NavLink
                    key={item.id}
                    label={
                      <Group gap="xs" wrap="nowrap">
                        <Text size="xs" style={{ flex: 1 }} truncate>{item.label}</Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={VERDICT_COLORS[item.verdict]}
                        >
                          {item.verdict}
                        </Badge>
                      </Group>
                    }
                    description={`${item.loc} LOC`}
                    active={selectedId === item.id}
                    onClick={() => setSelectedId(item.id)}
                    variant="filled"
                    styles={{
                      label: { fontSize: 'var(--mantine-font-size-xs)' },
                      description: { fontSize: '10px' },
                    }}
                  />
                ))}
              </NavLink>
            ))}
          </Stack>
        </ScrollArea>
      </Box>

      {/* Main content */}
      <Box style={{ flex: 1, overflow: 'auto' }} p="md">
        {selected ? (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Group gap="sm">
                  <Title order={3}>{selected.label}</Title>
                  <Badge
                    variant="filled"
                    color={VERDICT_COLORS[selected.verdict]}
                    size="sm"
                  >
                    {selected.verdict}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">{selected.description}</Text>
              </div>
              <Group gap="xs">
                <Badge variant="light" size="sm">{selected.category}</Badge>
                <Badge variant="light" color="gray" size="sm">{selected.loc} LOC</Badge>
              </Group>
            </Group>

            <Card withBorder p={0} style={{ minHeight: 400 }}>
              <FormErrorBoundary componentId={selected.id}>
                <Suspense
                  fallback={
                    <Center h={400}>
                      <Stack ta="center" gap="sm">
                        <Loader type="dots" />
                        <Text size="sm" c="dimmed">Loading {selected.label}...</Text>
                      </Stack>
                    </Center>
                  }
                >
                  <Box p="md">
                    {selected.needsProvider ? (
                      <GalleryProvider>
                        {React.createElement(
                          selected.component as React.ComponentType<Record<string, unknown>>,
                          selected.props ?? {},
                        )}
                      </GalleryProvider>
                    ) : (
                      React.createElement(
                        selected.component as React.ComponentType<Record<string, unknown>>,
                        selected.props ?? {},
                      )
                    )}
                  </Box>
                </Suspense>
              </FormErrorBoundary>
            </Card>
          </Stack>
        ) : (
          <Center h={400}>
            <Stack ta="center" gap="sm">
              <IconForms size={48} opacity={0.3} />
              <Text c="dimmed">Select a form from the sidebar</Text>
            </Stack>
          </Center>
        )}
      </Box>
    </Box>
  );
}
